import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import EdgeMarkers from '../roadmap-utility/EdgeMarkers';
import NodeContentViewer from './NodeContentViewer';
import cssClass from './RoadmapScreen.module.css';
import RoadmapSVGViewer from './RoadmapSVGViewer';
import html2canvas from 'html2canvas';
import backIcon from '../../assets/roadmap-screen/back-icon-black.svg';
import bookmarkIcon from '../../assets/roadmap-screen/bookmark.svg';
import bookmarkFilledIcon from '../../assets/roadmap-screen/bookmark-filled.svg';
import downloadIcon from '../../assets/roadmap-screen/download.svg';
import refreshIcon from '../../assets/roadmap-screen/refresh-icon.svg';
import shareIcon from '../../assets/roadmap-screen/share.svg';
import zoomInIcon from '../../assets/roadmap-screen/zoom-in.svg';
import zoomOutIcon from '../../assets/roadmap-screen/zoom-out.svg';
import emailIcon from '../../assets/roadmap-screen/email-icon.svg';
import callIcon from '../../assets/roadmap-screen/call-icon.svg';
import Header from '../Header';
import Footer from '../Footer';
import { changePageTitle } from '../../utils/constants';
import { ClientRoadmapContext } from '../../context/ClientRoadmapContext';
import { addCopyright } from '../../utils/helper';
import { AnimatePresence, motion } from 'framer-motion';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import Skeleton from '../roadmap-utility/Skeleton';
import { useNotification } from '../../hooks/useNotification';
import { v4 as uuid4 } from 'uuid';
import jsPDF from 'jspdf';

/**
 * @component RoadmpScreen
 * @param {Object} props - The props object.
 * @param {String} props.mode - Denotes the mode of the roadmap screen. For creators roadmap screen can be preview and for other users it will be live (default value).
 * @description This component is used to render the roadmap screen. It contains the roadmap graph and the node content viewer. It renders generated SVG from the roadmap graph and allows the user to view the content of each node by clicking on it. It also fetches the roadmap data and user learning progress from the server.
 * @returns {JSX.Element}
 */
const RoadmpScreen = ({ mode = 'live' }) => {
  // User details from UserContext
  const { userDetails } = useContext(UserContext);
  // Client roadmap details from ClientRoadmapContext
  const {
    nodes,
    setNodes,
    edges,
    setEdges,
    learningProgress,
    setLearningProgress,
    setContentCompleted,
    roadmapTitle,
    setRoadmapTitle,
    setIsBookmarked,
    setTotalRoadmapContent,
    setGrossRoadmapContent,
    premiumAccessData,
    setPremiumAccessData,
    roadmapPrice,
    setRoadmapPrice,
    selectedNode,
    setSelectedNode,
    setIsRoadmapFetching,
  } = useContext(ClientRoadmapContext);

  // This custom hook give the current window width of the user
  const { width } = useWindowDimensions();
  // Custom hook to show notifications
  const { showLoadingNotification, resolveNotification, rejectNotification } =
    useNotification();
  // Get roadmap ID from the URL
  const { roadmap_id } = useParams();
  // Object ot get query params
  const [searchParams] = useSearchParams();
  // Navigation function to navigate arround routes
  const navigate = useNavigate();
  // Roadmap container reference
  const roadmapContainerRef = useRef(null);

  /**
   * Fetches the roadmap data from the backend and updates the local state with nodes, edges, and metadata.
   *
   * @async
   * @function fetchRoadmapData
   * @description
   * Initiates a request to the server to retrieve full roadmap details, including the nodes, edges, title, price, and premium access data.
   * Sets these values in the component state and updates the page title.
   * If the request fails, redirects the user to the homepage.
   *
   * @returns {Promise<void>}
   *
   * @see setNodes - Sets the graph nodes for the roadmap.
   * @see setEdges - Sets the graph edges for the roadmap.
   * @see setRoadmapTitle - Sets the roadmap title.
   * @see setPremiumAccessData - Sets premium access data (e.g., paywall logic).
   * @see setRoadmapPrice - Sets the price for the roadmap.
   * @see setTotalRoadmapContent - Tracks total content for progress display.
   * @see setIsBookmarked - Sets whether the roadmap is bookmarked by the user.
   * @see changePageTitle - Changes the browser tab's title to match the roadmap title.
   * @see navigate - Redirects user to the homepage if the fetch fails.
   */
  const fetchRoadmapData = async () => {
    setIsRoadmapFetching(true);
    const response = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/r/get-roadmap?e=${userDetails?.email}&r=${roadmap_id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // credentials: 'include',
      },
    );
    const responseData = await response.json();
    if (response.status === 200) {
      setNodes(
        responseData?.g_data?.nodes.map((node) => ({
          ...node,
          selected: false,
        })),
      );
      setEdges(responseData?.g_data?.edges);
      setRoadmapTitle(responseData?.title);
      setPremiumAccessData(responseData?.premium_access);
      setRoadmapPrice(responseData?.price);
      setTotalRoadmapContent(responseData?.total_content || 0);
      setGrossRoadmapContent(responseData?.gross_content || 0);
      setIsBookmarked(responseData?.is_bookmarked || false);
      changePageTitle(`${responseData?.title} | SETU Roadmaps`);
    } else {
      navigate('/');
    }
    setIsRoadmapFetching(false);
  };

  /**
   * Fetches the user's learning progress for the specified roadmap.
   *
   * @async
   * @function fetchUserLearningProgress
   * @description
   * Sends a request to the server to get the learning progress for the current user on a specific roadmap.
   * Updates the learning progress and content completion in the state.
   *
   * @returns {Promise<void>}
   *
   * @see setLearningProgress - Updates the learning progress state.
   * @see setContentCompleted - Tracks the number of content pieces completed by the user.
   */
  const fetchUserLearningProgress = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/r/get-learning-progress?e=${userDetails?.email}&r_id=${roadmap_id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          // credentials: 'include',
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        setLearningProgress(responseData?.learning_progress);
        setContentCompleted(responseData?.content_completed || 0);
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Handles the node click interaction on the roadmap.
   *
   * @function handleNodeClick
   * @description
   * This function is triggered when a user clicks on a node within the roadmap. If the node type is not one of the following:
   * - 'paragraph'
   * - 'title'
   * - 'subtopicGroup'
   * It sets the clicked node as the selected node and navigates to the specific node's URL, updating the browser history without reloading.
   *
   * @param {Object} node - The node object that was clicked.
   * @param {string} node.id - The unique identifier of the node.
   * @param {string} node.type - The type of the node (used to filter valid node clicks).
   */
  const handleNodeClick = (node) => {
    if (
      ['title', 'topic', 'topicGroup', 'subtopicGroup'].includes(node?.type)
    ) {
      return;
    }
    setSelectedNode(node);
    if (mode === 'preview') {
      navigate(`/preview/${roadmap_id}?node=${node.id}`, { replace: true });
    } else {
      navigate(`/roadmap/${roadmap_id}?node=${node.id}`, { replace: true });
    }
  };

  /**
   * Closes the popup and resets the selected node.
   *
   * @function closePopup
   * @description
   * This function is triggered when a user decides to close the node popup. It resets the selected node state and navigates
   * to the roadmap without the node query in the URL but do not add to browser history stack.
   */
  const closePopup = () => {
    setSelectedNode(null);
    if (mode == 'preview');
    if (mode === 'preview') {
      navigate(`/preview/${roadmap_id}`, { replace: true });
    } else {
      navigate(`/roadmap/${roadmap_id}`, { replace: true });
    }
  };

  /**
   * Refreshes the roadmap data and user progress when the refresh button is clicked.
   *
   * @async
   * @function onRefreshButtonClick
   * @description
   * This function triggers a refresh of the roadmap data and user learning progress, showing a loading notification while
   * the data is being fetched and resolving it with a success notification once complete.
   *
   * @returns {Promise<void>} - Resolves after data fetching and updates.
   */
  const onRefreshButtonClick = async () => {
    const toastId = uuid4();
    showLoadingNotification(toastId, 'Updating Preview');

    await fetchRoadmapData();
    await fetchUserLearningProgress();

    resolveNotification(toastId, 'Preview updated!!');
  };

  /**
   * Initializes the fetching of roadmap data and user progress on component mount and when `roadmap_id` changes.
   *
   * @function useLayoutEffect
   * @description
   * This hook is triggered on the initial load and whenever the `roadmap_id` changes, fetching both the roadmap data
   * and user progress via the `fetchRoadmapData` and `fetchUserLearningProgress` functions.
   */
  useLayoutEffect(() => {
    fetchRoadmapData();
    fetchUserLearningProgress();
  }, [roadmap_id]);
  /**
   * Handles the URL query string to check if a node ID exists, and scrolls to and selects the node if found.
   *
   * @function useEffect
   * @description
   * This effect checks for a `node` query parameter in the URL when the `nodes` array is updated. If a node ID is found,
   * it locates the node in the list and scrolls to it in the view, selecting the node. The effect runs whenever `nodes`
   * changes.
   */
  useEffect(() => {
    const node_id = searchParams.get('node');
    if (node_id) {
      const node = nodes.find((n) => n.id == node_id);
      if (node) {
        const el = document.querySelector(`[data-node-id="${node_id}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus?.();
        }
        setSelectedNode(node);
      }
    }
  }, [nodes]);

  /**
   * Polls the server every 10 seconds to refresh the roadmap and user learning progress when in "preview" mode.
   *
   * @function useEffect
   * @description
   * When the mode is 'preview', this effect sets an interval to periodically refresh the roadmap preview by calling
   * `onRefreshButtonClick` every 10 seconds. It cleans up the interval when the component is unmounted.
   */
  useEffect(() => {
    if (mode === 'preview') {
      // polling
      const intervalId = setInterval(() => {
        fetchRoadmapData();
        fetchUserLearningProgress();
      }, 10000);
      return () => clearInterval(intervalId);
    }
  }, []);

  // Framer Animation variants
  const backdropVariants = {
    hidden: {
      opacity: 0,
      transition: { duration: 0.5 },
    },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const modalVariants =
    width < 768
      ? {
          hidden: {
            y: '100%',
            opacity: 0,
            transition: {
              type: 'tween',
              duration: 0.5,
              ease: 'easeInOut',
            },
          },
          visible: {
            y: 0,
            opacity: 1,
            transition: {
              type: 'tween',
              duration: 0.5,
              ease: 'easeOut',
            },
          },
        }
      : {
          hidden: {
            x: '100%',
            transition: {
              type: 'tween',
              duration: 0.5,
              ease: 'easeInOut',
            },
          },
          visible: {
            x: 0,
            transition: {
              type: 'tween',
              duration: 0.5,
              ease: 'easeOut',
            },
          },
        };

  return (
    <div className={cssClass['main-container']}>
      <Header />
      <div
        className={cssClass['container']}
        id="roadmap-screen-container"
        ref={roadmapContainerRef}
      >
        <RoadmapHeroSection
          roadmap_id={roadmap_id}
          mode={mode}
          onRefreshButtonClick={onRefreshButtonClick}
        />

        <RoadmapUtilityPanel />

        <RoadmapDiagramRenderer
          nodes={nodes}
          edges={edges}
          learningProgress={learningProgress}
          onNodeClick={handleNodeClick}
        />

        <AnimatePresence>
          {selectedNode && (
            <>
              <motion.div
                className={cssClass['backdrop']}
                onClick={closePopup}
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={backdropVariants}
                key="backdrop"
              ></motion.div>
              <motion.div
                className={cssClass['modal']}
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={modalVariants}
                onClick={(e) => e.stopPropagation()}
                key="modal"
              >
                <NodeContentViewer
                  selectedNode={selectedNode}
                  learningProgress={learningProgress}
                  setLearningProgress={setLearningProgress}
                  closePopup={closePopup}
                  premiumAccessData={premiumAccessData}
                  roadmapTitle={roadmapTitle}
                  roadmapPrice={roadmapPrice}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      <GetInTouchSection />
      {/* <RelatedCoursesSection /> */}
      <Footer />
    </div>
  );
};

const RoadmapHeroSection = ({ roadmap_id, mode, onRefreshButtonClick }) => {
  const { userDetails } = useContext(UserContext);
  const {
    roadmapTitle,
    isBookmarked,
    setIsBookmarked,
    roadmapPrice,
    premiumAccessData,
    totalRoadmapContent,
    grossRoadmapContent,
    contentCompleted,
    isRoadmapFetching,
  } = useContext(ClientRoadmapContext);
  const {
    showNotification,
    showLoadingNotification,
    resolveNotification,
    rejectNotification,
  } = useNotification();
  // State to store the percentage of user learning progress
  const [progressPercentage, setprogressPercentage] = useState(0);
  // State to check the roamdap is bookmarked or not
  const [isBoomarkingRoadmap, setIsBoomarkingRoadmap] = useState(false);
  // State to check the roadmap is downloading or not
  const [isDownloadingRoadmap, setIsDownloadingRoadmap] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.paymentSuccess) {
      showNotification(
        'success',
        'You have upgraded from free to premium access. All the premium conents are unlocked',
        { position: 'top-center' },
      );
      setTimeout(() => {
        showNotification(
          'info',
          'Note : The topic/sub-topic you have marked as done earlier, the premium content in these also included in progress',
          { position: 'top-center' },
        );
      }, 3000);

      // Clean up state so it doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Navigate to home path `/` when back button clicked
  const handleBackButtonClick = () => {
    navigate('/');
  };

  // Navigate to payment page when `Buy Now` clicked
  const handleBuyContentButtonClick = () => {
    navigate(`/pay`, {
      state: {
        price: roadmapPrice,
        roadmap_id: roadmap_id,
        roadmap_title: roadmapTitle,
        redirectTo: window.location.pathname,
      },
    });
  };

  /**
   * Updates metadata related to user interactions (such as bookmarking, sharing, and downloading) for a specific roadmap.
   *
   * @async
   * @function updateMetadata
   * @param {Object} data - The data to send to the server for metadata update.
   * @param {string} data.type - The type of event being tracked (e.g., 'bookmark', 'share', 'download').
   * @param {boolean} [data.is_adding] - (For 'bookmark' type) Whether the event is adding or removing a bookmark.
   * @param {string} [data.mode] - (For 'share' type) The mode of sharing (default is 'others').
   * @description
   * Sends an HTTP POST request to update metadata for a specific roadmap based on user interaction.
   * Handles different types of metadata events (bookmark, share, download) and sends the corresponding data.
   * If successful, the function returns true; otherwise, it returns false.
   *
   * @returns {Promise<boolean>} - Resolves to true if the update was successful, otherwise false.
   */
  const updateMetadata = async (data) => {
    try {
      let payload = {
        e: userDetails?.email,
        event: {
          type: data.type,
        },
      };
      if (data.type === 'bookmark') {
        payload.event.is_adding = data.is_adding;
      } else if (data.type == 'share') {
        payload.event.mode = data?.mode || 'others';
      } else if (data.type == 'download') {
        // do not need to explecetly handle for download
      }

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/r/metadata/${roadmap_id}`,
        {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        return true;
      } else {
      }
    } catch (error) {
      console.log(error);
    }
    return false;
  };

  /**
   * Toggles the bookmark status of a roadmap and updates the metadata.
   *
   * @async
   * @function updateBookmark
   * @description
   * This function is triggered when the user interacts with the bookmark button. It updates the bookmark status in the state
   * and sends the metadata update request to the server. It also shows appropriate loading and success/error notifications.
   *
   * @returns {Promise<void>}
   */
  const updateBookmark = async () => {
    setIsBoomarkingRoadmap(true);
    const toastId = uuid4();
    showLoadingNotification(
      toastId,
      !isBookmarked ? 'Adding to bookmarked' : 'Removing from bookmark',
    );

    const verdict = await updateMetadata({
      type: 'bookmark',
      is_adding: !isBookmarked,
    });
    if (verdict) {
      setIsBookmarked(!isBookmarked);
      resolveNotification(
        toastId,
        !isBookmarked ? 'Added to bookmarked' : 'Removed from bookmark',
      );
    } else {
      rejectNotification(toastId, 'Something went wrong. Please try again');
    }
    setIsBoomarkingRoadmap(false);
  };

  const downloadRoadmap = async (format) => {
    setIsDownloadingRoadmap(true);
    const toastId = uuid4();
    showLoadingNotification(toastId, 'Downloading roadmap. Please wait');

    try {
      // Get the original roadmap element
      const roadmapElement = document.getElementById('react-flow-wrapper');
      const knowledgeLegendElement = document.getElementById(
        'knowledge-level-legend',
      );

      // Create a hidden container for rendering
      const hiddenContainer = document.createElement('div');
      hiddenContainer.style.position = 'fixed';
      hiddenContainer.style.top = '0';
      hiddenContainer.style.left = '0';
      hiddenContainer.style.zIndex = '-9999'; // Hide it off-screen instead of visible with high z-index
      hiddenContainer.style.display = 'flex';
      hiddenContainer.style.flexDirection = 'column';
      hiddenContainer.style.justifyContent = 'center';
      hiddenContainer.style.gap = '10px';

      document.body.appendChild(hiddenContainer);

      // Clone the roadmap element
      const clone = roadmapElement.cloneNode(true);
      const knowledgeLevelLegendClone = knowledgeLegendElement.cloneNode(true);

      // Get the actual svg element inside the clone
      const svgElement = clone.querySelector('svg');
      if (!svgElement) {
        console.error('SVG element not found in the roadmap');
        return;
      }

      // Get the original dimensions and calculate aspect ratio
      const originalWidth =
        svgElement.viewBox.baseVal.width || svgElement.width.baseVal.value;
      const originalHeight =
        svgElement.viewBox.baseVal.height || svgElement.height.baseVal.value;
      const aspectRatio = originalHeight / originalWidth;

      // Set the fixed width and calculate proportional height
      const targetWidth = 1200;
      const targetHeight = Math.round(targetWidth * aspectRatio);

      // Configure the container with the target dimensions
      hiddenContainer.style.width = `${targetWidth}px`;
      hiddenContainer.style.height = `${targetHeight + 200}px`;

      // Configure the clone for proper rendering
      clone.style.width = `${targetWidth}px`;
      clone.style.height = `${targetHeight}px`;
      clone.style.transform = 'scale(1)'; // Reset any scaling
      clone.style.display = 'block';
      clone.style.overflow = 'visible';
      clone.style.padding = '5px';

      // Remove unwanted elements
      const elementsToRemove = clone.querySelectorAll(
        '#knowledge-level-legend, #progress-status-div',
      );
      elementsToRemove.forEach((el) => el.remove());

      // Fix any label width issues
      const elementToUpdate = clone.querySelectorAll('#node-lable-ct');
      elementToUpdate.forEach((el) => (el.style.width = '100%'));

      knowledgeLevelLegendClone.style.width = '100%';
      knowledgeLevelLegendClone.style.margin = '20px';
      knowledgeLevelLegendClone.style.justifyContent = 'flex-start';
      const knlLeft = knowledgeLevelLegendClone.querySelectorAll('#knl_left');
      const knlRight = knowledgeLevelLegendClone.querySelectorAll('#knl_right');
      knlLeft.forEach(
        (kl) => ((kl.style.height = '16px'), (kl.style.width = '16px')),
      );
      knlRight.forEach((kr) => (kr.style.fontSize = '16px'));

      // Watermark Text
      // addWatermark(clone, 'SETU ROADMAPS');

      // Add the clone to the hidden container
      hiddenContainer.appendChild(knowledgeLevelLegendClone);
      hiddenContainer.appendChild(clone);

      // Force layout calculation (in a way that won't trigger ESLint)
      const _ = hiddenContainer.getBoundingClientRect(); // This forces a layout recalculation

      // Copyright Text at Bottom
      addCopyright(hiddenContainer);

      // Convert to image with the exact dimensions
      const canvas = await html2canvas(hiddenContainer, {
        allowTaint: true,
        backgroundColor: '#fff',
        useCORS: true,
        foreignObjectRendering: true,
        width: targetWidth,
        height: targetHeight + 200,
        scale: 2,
      });

      // Remove the hidden container after rendering
      document.body.removeChild(hiddenContainer);

      if (format === 'pdf') {
        const imgData = canvas.toDataURL('image/jpeg');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'px',
          format: [canvas.width, canvas.height],
          compress: true,
        });

        pdf.addImage(imgData, 'JEPG', 0, 0, canvas.width, canvas.height);
        pdf.save(`${roadmapTitle}.pdf`);
      } else {
        const link = document.createElement('a');
        link.href = canvas.toDataURL(`image/${format}`);
        link.download = `${roadmapTitle}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      resolveNotification(toastId, 'Downloaded successfully');
    } catch (error) {
      console.error('Error in html2canvas:', error);
      rejectNotification(toastId, 'Something went wrong. Please try again');
    } finally {
      setIsDownloadingRoadmap(false);
    }
  };

  /**
   * Calculates and updates the progress percentage based on the content completed in the roadmap.
   *
   * @function useEffect
   * @description
   * This effect is triggered whenever the `contentCompleted` or `totalRoadmapContent` values change. It calculates the
   * percentage of the roadmap content completed by dividing the `contentCompleted` by `totalRoadmapContent` and multiplying
   * by 100. The result is rounded and then set as the new `progressPercentage` state.
   *
   * If `totalRoadmapContent` is zero, the progress percentage will not be calculated to avoid division by zero errors.
   */
  useEffect(() => {
    if (totalRoadmapContent != 0) {
      const newProgressPercentage = Math.round(
        (contentCompleted * 100) / totalRoadmapContent,
      );
      setprogressPercentage(newProgressPercentage);
    }
  }, [contentCompleted, totalRoadmapContent]);

  return (
    <>
      {isRoadmapFetching && mode !== 'preview' ? (
        <>
          <RoadmapHeroSectionSkeletonLoader />
        </>
      ) : (
        <div className={cssClass['hero-container']}>
          <div className={cssClass['hero-container-head']}>
            <div
              className={cssClass['back-section']}
              onClick={handleBackButtonClick}
            >
              <img src={backIcon} alt="back-icon" />
              <span className={cssClass['back-section-text']}>
                View Dashboard Page
              </span>
            </div>
            <div className={cssClass['utility-section']}>
              {mode === 'preview' && (
                <>
                  <img
                    src={refreshIcon}
                    alt="refresh"
                    onClick={onRefreshButtonClick}
                    title="refresh preview"
                  />
                  <button disabled={isBoomarkingRoadmap} title="bookmark roadmap">
                    <img
                      src={isBookmarked ? bookmarkFilledIcon : bookmarkIcon}
                      alt="bookmark"
                      onClick={updateBookmark}
                    />
                  </button>
                  <button disabled={isDownloadingRoadmap} title="download roadmap">
                    <img
                      src={downloadIcon}
                      alt="download"
                      onClick={() => downloadRoadmap('pdf')}
                    />
                  </button>
                  <button>
                    <img src={shareIcon} alt="share" title="share" />
                  </button>
                </>
              )}
            </div>
          </div>
          <div className={cssClass['hero-container-middle']}>
            <div className={cssClass['roadmap-title-ct']}>
              <div className={cssClass['roadmap-title']}>{roadmapTitle}</div>
              {premiumAccessData && (
                <div className={cssClass['premium-access-chip']}>
                  <p>Premium Access</p>
                </div>
              )}
            </div>
            {/* <button className={cssClass['start-learning-btn']}>
              Start Learning
            </button> */}
          </div>
          <div className={cssClass['hero-container-footer']}>
            {!premiumAccessData && (
              <>
                <div className={cssClass['hero-container-footer-head']}>
                  How to navigate your learning journey
                </div>
                <div className={cssClass['hero-container-footer-body']}>
                  <div className={cssClass['hero-container-footer-content']}>
                    Lorem ipsum dolor sit amet consectetur. Nisl urna
                    ullamcorper. Lorem ipsum dolor sit amet consectetur. Nisl
                    urna ullamcorper.
                  </div>
                  <div className={cssClass['hero-container-footer-content']}>
                    Lorem ipsum dolor sit amet consectetur. Nisl urna
                    ullamcorper.
                  </div>
                  {/* <div className={cssClass['hero-container-footer-content']}>
                    Unlock all the premium content for{' '}
                    <span className={cssClass['roadmap-price']}>
                      ₹{roadmapPrice}
                    </span>{' '}
                    <span
                      className={cssClass['buy-now']}
                      onClick={handleBuyContentButtonClick}
                    >
                      Buy Now
                    </span>
                  </div>*/}
                </div>
              </>
            )}

            {/* <div className={cssClass['progress-container']}> */}
            <div className={cssClass['progress-ct-bar']}>
              <div className={cssClass['total-progress-count']}>
                <p>Total {totalRoadmapContent} {!premiumAccessData ? `/ ${grossRoadmapContent}` : ''} Content</p>
                {!premiumAccessData && (
                  <span className={cssClass['content-chip']}>Free</span>
                )}
              </div>
              <div>
                <span className={cssClass['progress-percentage']}>
                  {progressPercentage}%
                </span>{' '}
                <span className={cssClass['progress-count']}>
                  {contentCompleted} Of {totalRoadmapContent} Done
                </span>
              </div>
              <div className={cssClass['progress-bar-outer']}>
                <div
                  className={cssClass['progress-bar-inner']}
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
            {/* </div> */}
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Renders a utility panel for the roadmap screen, including the knowledge level legend and zoom controls.
 *
 * @component
 * @description
 * This component serves as a container for various utility tools related to the roadmap. It includes:
 * - A `KnowledgeLevelLegend` component that displays a legend for different knowledge levels.
 * - A `ZoomInZoomOutSection` component that allows users to zoom in and out on the roadmap view.
 *
 * @returns {JSX.Element} - The rendered utility panel containing zoom and knowledge level legend sections.
 */
const RoadmapUtilityPanel = () => {
  return (
    <div className={cssClass['utility-ct']}>
      <KnowledgeLevelLegend />
      <ZoomInZoomOutSection />
    </div>
  );
};

/**
 * Provides zoom in and zoom out controls to adjust the zoom level of the roadmap.
 *
 * @component
 * @description
 * This component consists of two clickable icons: one for zooming in and one for zooming out. The zoom level is
 * controlled via the `zoomLevel` state in the `ClientRoadmapContext`. The zoom level can range from 1 to 3, with
 * each click modifying the zoom by 0.2.
 *
 * @returns {JSX.Element} - The rendered zoom controls with click events to zoom in and zoom out.
 */
const ZoomInZoomOutSection = () => {
  const MAX_ZOOM_LEVEL = 5;
  const MIN_ZOOM_LEVEL = 1;

  const { zoomLevel, setZoomLevel } = useContext(ClientRoadmapContext);

  // Handles the zoom-in action, increasing the zoom level by 0.2, but not exceeding a maximum of 3.
  const handleZoomIn = () => {
    setZoomLevel(Math.min(MAX_ZOOM_LEVEL, zoomLevel + 0.2));
  };

  // Handles the zoom-out action, decreasing the zoom level by 0.2, but not going below a minimum of 1.
  const handleZoomOut = () => {
    setZoomLevel(Math.max(MIN_ZOOM_LEVEL, zoomLevel - 0.2));
  };

  return (
    <div className={cssClass['zizo-inner']}>
      <img src={zoomInIcon} onClick={handleZoomIn} alt="zoom-in" />
      <img src={zoomOutIcon} onClick={handleZoomOut} alt="zoom-out" />
    </div>
  );
};

const RoadmapDiagramRenderer = ({
  nodes,
  edges,
  learningProgress,
  onNodeClick,
}) => {
  const { zoomLevel } = useContext(ClientRoadmapContext);

  return (
    <div className={cssClass['react-flow']} id="react-flow-wrapper">
      {nodes.length > 0 && edges.length >= 0 && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'top left',
            transition: 'transform 0.3s ease-in-out',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <RoadmapSVGViewer
            roadmap_graph={{ nodes, edges }}
            learningProgress={learningProgress}
            onNodeClick={onNodeClick}
          />
          {/* <KnowledgeLevelLegend /> */}
          <div style={{ position: 'absolute', top: '0', left: '0' }}>
            <EdgeMarkers />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Renders a legend displaying different knowledge levels for the roadmap content.
 *
 * @component
 * @description
 * This component displays a visual legend indicating the three knowledge levels associated with the roadmap content:
 * - **Must Know** (represented by a solid color)
 * - **Good to Know** (represented by a different color)
 * - **Suggested Learning** (represented by another color)
 * Each level is displayed with a label on the right and a color indicator on the left.
 *
 * @returns {JSX.Element} - The rendered knowledge level legend with visual indicators for each level.
 */
const KnowledgeLevelLegend = () => {
  return (
    <div
      className={cssClass['knowledge-level-legend']}
      id="knowledge-level-legend"
    >
      <div className={cssClass['knowledge-level-child']}>
        <div
          className={`${cssClass['knl-left']} ${cssClass['must-know']}`}
          id="knl_left"
        ></div>
        <div className={cssClass['knl-right']} id="knl_right">
          Must Know
        </div>
      </div>
      <div className={cssClass['knowledge-level-child']}>
        <div
          className={`${cssClass['knl-left']} ${cssClass['good-to-know']}`}
          id="knl_left"
        ></div>
        <div className={cssClass['knl-right']} id="knl_right">
          Good to Know
        </div>
      </div>
      <div className={cssClass['knowledge-level-child']}>
        <div
          className={`${cssClass['knl-left']} ${cssClass['suggested-learning']}`}
          id="knl_left"
        ></div>
        <div className={cssClass['knl-right']} id="knl_right">
          Suggested Learning
        </div>
      </div>
    </div>
  );
};

/**
 * Renders a "Get in Touch" section with contact information, including email and phone details.
 *
 * @component
 * @description
 * This component provides a user interface for visitors to get in touch with the team. It includes:
 * - A heading titled "Get in Touch."
 * - A descriptive text section explaining the contact methods.
 * - Contact information with email and phone details, where users can either click to email or call/WhatsApp directly.
 *
 * The section also includes relevant icons for email and phone contact options to enhance the visual appeal and ease of use.
 *
 * @returns {JSX.Element} - The rendered "Get in Touch" section containing contact information.
 */
const GetInTouchSection = () => {
  return (
    <div className={cssClass['get-in-touch-main']}>
      <div className={cssClass['get-in-touch-head']}>Get in Touch</div>
      <div className={cssClass['get-in-touch-text']}>
      We’re here to support your learning journey — reach out to us for any queries, feedback. {' '}
      </div>
      <div className={cssClass['get-in-touch-contact-info']}>
        <div className={cssClass['contact-email']}>
          <img src={emailIcon} alt="" />
          <div>Email Us</div>
          <a href="mailto:mitra@setuschool.com" className={cssClass['email']}>
            mitra@setuschool.com
          </a>
        </div>
        <div className={cssClass['contact-phone']}>
          <img src={callIcon} alt="" />
          <div>Call/Whatsapp Us</div>
          <div className={cssClass['phone']}>+91 8100551189</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders a list of related courses with clickable links that navigate to their respective pages.
 *
 * @component
 * @description
 * This component displays a "Related Courses" section, which lists a set of recommended courses based on the context.
 * - Each course is displayed as a clickable item.
 * - On clicking a course, the user is navigated to the respective course page.
 *
 * The list of related courses is defined in the component's state and consists of course names and corresponding URLs.
 *
 * @returns {JSX.Element} - The rendered "Related Courses" section containing a list of course names.
 */
const RelatedCoursesSection = () => {
  const [relatedCourses, setRelatedCourses] = useState([
    {
      name: 'Machine Learning',
      url: '/course/machine-learning',
    },
    {
      name: 'AI & LLM',
      url: '/course/ai-and-llm',
    },
    {
      name: 'Data Scientist',
      url: '/course/data-scientist',
    },
    {
      name: 'Gen AI',
      url: '/course/gen-ai',
    },
    {
      name: 'Data Engineering',
      url: '/course/data-engineering',
    },
  ]);
  const navigate = useNavigate();

  const handleCourseClick = (course) => {
    navigate(course.url);
  };

  return (
    <div className={cssClass['related-courses-main-ct']}>
      <div className={cssClass['related-courses-head']}>Related Courses</div>
      <div className={cssClass['related-courses-ct']}>
        {relatedCourses.map((course) => (
          <div
            key={course?.name}
            className={cssClass['related-course']}
            onClick={() => handleCourseClick(course)}
          >
            {course?.name}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Skeleton loader for Roadmap Hero Section
 * @returns {JSX.Element}
 */
const RoadmapHeroSectionSkeletonLoader = () => {
  const { width } = useWindowDimensions();
  return (
    <div className={cssClass['hero-container']}>
      <div className={cssClass['hero-container-head']}>
        <Skeleton variant="text" height={40} width={width <= 768 ? 40 : 215} />
        {/* <Skeleton variant="text" height={40} width={116} /> */}
      </div>
      <div className={cssClass['hero-container-middle']}>
        <Skeleton variant="text" height={44} width={'75%'} />
        {/* <Skeleton variant="button" height={44} width={218} /> */}
      </div>
      <Skeleton variant="card" height={100} width={'100%'} />
    </div>
  );
};

export default RoadmpScreen;
